import { forwardRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSmartNotifications } from "@/hooks/useSmartNotifications";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { useAnalytics, ANALYTICS_EVENTS } from "@/hooks/useAnalytics";

const PRIORITY_STYLES = {
  high: "border-destructive/30 bg-destructive/5",
  normal: "border-primary/20 bg-primary/5",
  low: "border-border bg-muted/30",
};

const SmartNotificationToast = forwardRef<HTMLDivElement>((_props, ref) => {
  const { notifications, dismissNotification } = useSmartNotifications();
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const visible = notifications.slice(0, 3);

  if (visible.length === 0) return null;

  return (
    <div ref={ref} className="fixed top-4 right-4 left-4 sm:left-auto sm:w-96 z-[90] space-y-2">
      <AnimatePresence>
        {visible.map((notif) => (
          <motion.div
            key={notif.id}
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            className={cn(
              "rounded-xl border p-3 shadow-lg backdrop-blur-sm",
              PRIORITY_STYLES[notif.priority]
            )}
          >
            <div className="flex items-start gap-3">
              <span className="text-xl shrink-0">{notif.emoji}</span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">{notif.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{notif.body}</p>
              </div>
              {notif.dismissable && (
                <button
                  onClick={() => {
                    track(ANALYTICS_EVENTS.NOTIFICATION_DISMISSED, { context: notif.type });
                    dismissNotification(notif.id);
                  }}
                  className="text-muted-foreground hover:text-foreground p-1"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {notif.actionLabel && notif.actionLink && (
              <Button
                size="sm"
                variant="outline"
                className="mt-2 text-xs h-7 rounded-lg"
                onClick={() => {
                  track(ANALYTICS_EVENTS.NOTIFICATION_CLICKED, { context: notif.type });
                  navigate(notif.actionLink!);
                  dismissNotification(notif.id);
                }}
              >
                {notif.actionLabel} <ChevronRight className="h-3 w-3 ml-0.5" />
              </Button>
            )}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
});

SmartNotificationToast.displayName = "SmartNotificationToast";

export default SmartNotificationToast;
